<template>
    <v-app-bar app clipped-left flat dark height="56" class="app-bar">
        <v-app-bar-nav-icon aria-label="Menú" @click.stop="$emit('toggle-nav')"></v-app-bar-nav-icon>
        <span class="app-bar__brand d-none d-md-flex">Granja San Fernando</span>
        <span v-if="sucursal" class="app-bar__chip" :title="sucursal">
            <i class="fas fa-store" aria-hidden="true"></i>
            <span class="app-bar__chip-text">{{ sucursal }}</span>
        </span>
        <v-spacer></v-spacer>

        <button type="button" class="app-bar__search d-none d-lg-flex" @click="$emit('search')">
            <i class="fas fa-search" aria-hidden="true"></i>
            <span>Buscar módulo</span>
            <kbd>Ctrl K</kbd>
        </button>
        <v-btn icon class="d-lg-none" aria-label="Buscar módulo" @click="$emit('search')">
            <i class="fas fa-search" aria-hidden="true"></i>
        </v-btn>
        <!-- Notificaciones ocultas por ahora: no hay avisos que mostrar todavía. -->
        <v-btn v-if="false" icon aria-label="Notificaciones">
            <i class="fas fa-bell" aria-hidden="true"></i>
        </v-btn>

        <!-- la barra siempre es `dark`: el menú sigue al tema de la app, no a la barra -->
        <v-menu offset-y left :light="!isDark" :dark="isDark" min-width="220">
            <template v-slot:activator="{ on, attrs }">
                <button type="button" class="app-bar__avatar" aria-label="Cuenta" v-bind="attrs" v-on="on">
                    <img :src="avatarUrl" alt="" />
                </button>
            </template>
            <v-list dense>
                <div class="app-bar__who">
                    <span class="app-bar__who-name">{{ name }}</span>
                    <span class="app-bar__who-role">{{ role }}</span>
                </div>
                <v-divider></v-divider>
                <v-list-item to="/controlPanel/profile">
                    <v-list-item-title>Perfil</v-list-item-title>
                </v-list-item>
                <v-list-item @click="toggleTheme">
                    <v-list-item-title>Tema oscuro</v-list-item-title>
                    <i v-if="isDark" class="fas fa-check app-bar__check" aria-hidden="true"></i>
                </v-list-item>
                <v-list-item @click="logout">
                    <v-list-item-title>Cerrar sesión</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>
        <v-btn icon class="app-bar__logout" aria-label="Cerrar sesión" title="Cerrar sesión" @click="logout">
            <i class="fas fa-sign-out-alt" aria-hidden="true"></i>
        </v-btn>
    </v-app-bar>
</template>
<script>
import { roleLabel } from '@/service/permiso/menuModel';
import { saveThemeDark } from '@/utils/themePreference';

/**
 * Barra superior: dónde estoy (sucursal), buscar módulo, avisos y la cuenta.
 * El título de cada pantalla ya no vive aquí sino en su `PageHeader`.
 *
 * Solo usa Vuetify donde no hay alternativa simple (`v-app-bar`, `v-menu`);
 * los iconos son `<i class="fas ...">`, que funcionan igual en Vuetify 2 y 3.
 * Migración a Vuetify 3: el slot del activador de `v-menu` pasa a `{ props }`
 * (`v-bind="props"`), y `v-list-item` toma `title` como prop.
 */
export default {
    name: 'AppBar',
    props: {
        sucursal: { type: String, default: '' },
        user: { type: Object, default: () => ({}) },
        avatarUrl: { type: String, default: '' }
    },
    computed: {
        name() {
            return this.user.username || this.user.nombre || '';
        },
        role() {
            return roleLabel(this.user.roleName);
        },
        isDark() {
            return this.$vuetify.theme.dark;
        }
    },
    methods: {
        // Claro <-> oscuro: se aplica al instante y se recuerda en el navegador.
        toggleTheme() {
            const dark = !this.$vuetify.theme.dark;
            this.$vuetify.theme.dark = dark;
            saveThemeDark(dark);
        },
        async logout() {
            await this.$store.dispatch('auth/logout');
            // replace: con push, "Atras" volveria al panel recien cerrado.
            this.$router.replace({ name: 'login' });
        }
    }
};
</script>
<style scoped>
/* Vuetify pinta `.theme--dark.v-app-bar.v-toolbar.v-sheet` en gris oscuro:
   la clase propia suma una más para que el token gane. La barra es `dark`
   para que hamburguesa, iconos y texto salgan en blanco sin tocarlos. */
.v-app-bar.v-toolbar.v-sheet.app-bar {
    background-color: var(--app-bar-bg);
    color: var(--app-on-bar);
}
.app-bar__brand {
    align-items: center;
    margin-right: 12px;
    font-size: 15px;
    font-weight: 600;
    white-space: nowrap;
}
.app-bar__chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    max-width: 220px;
    padding: 3px 10px;
    border: 1px solid rgba(255, 255, 255, .4);
    border-radius: 99px;
    font-size: 13px;
    color: var(--app-on-bar);
}
.app-bar__chip-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.app-bar__search {
    align-items: center;
    gap: 8px;
    width: 260px;
    margin-right: 8px;
    padding: 6px 10px;
    border: 1px solid rgba(255, 255, 255, .22);
    border-radius: 6px;
    background: rgba(255, 255, 255, .12);
    color: rgba(255, 255, 255, .85);
    font: inherit;
    font-size: 13px;
    text-align: left;
    cursor: pointer;
}
.app-bar__search:hover {
    background: rgba(255, 255, 255, .18);
}
.app-bar__search:focus-visible,
.app-bar__avatar:focus-visible {
    outline: 2px solid var(--app-on-bar);
    outline-offset: 2px;
}
.app-bar__search span {
    flex: 1;
}
.app-bar__search kbd {
    padding: 0 6px;
    border: 1px solid rgba(255, 255, 255, .35);
    border-radius: 4px;
    background: transparent;
    font: inherit;
    font-size: 11px;
}
.app-bar__avatar {
    margin-left: 4px;
    padding: 0;
    width: 36px;
    height: 36px;
    border: 0;
    border-radius: 50%;
    background: transparent;
    cursor: pointer;
}
.app-bar__avatar img {
    border: 2px solid rgba(255, 255, 255, .7);
    box-sizing: border-box;
    display: block;
    width: 32px;
    height: 32px;
    margin: 2px;
    border-radius: 50%;
    object-fit: cover;
    background: var(--app-surface);
}
.app-bar__logout {
    margin-left: 4px;
}
.app-bar__check {
    margin-left: 8px;
    color: var(--app-primary);
}
.app-bar__who {
    display: flex;
    flex-direction: column;
    padding: 10px 16px;
    line-height: 1.3;
}
.app-bar__who-name {
    font-size: 14px;
    font-weight: 600;
}
.app-bar__who-role {
    font-size: 12px;
    color: var(--app-text-muted);
}
</style>
